import React from 'react';
import PropTypes from 'prop-types';

import Text from 'relements/build/esm/input/text';
import Checkbox from 'relements/build/esm/input/checkbox';
import Radio from 'relements/build/esm/input/radio';
import Button from 'relements/build/esm/ui/button';
import Table from 'relements/build/esm/ui/table';

import { AlertTypes } from '@src/constants/MogamboConstants';
import Icon from '@src/components/Icon';
import {
  updateComponentModal,
  closeComponentModal,
  createAlert,
} from '@src/actions/BotActions';

import {
  SUPPORTED_VARIABLES,
  SUPPORTED_OPERATORS_BY_TYPE,
  FIXED_VALUE_OPERATORS,
  PROMPT_MAX_QUICK_REPLY,
  PROMPT_MAX_TITLE_LENGTH,
  PROMPT_MAX_QUICK_REPLY_TITLE_LENGTH,
  SUPPORTED_TYPES,
  SUPPORTED_OPERATORS,
} from '../../../const/promptTrigger';

// Local
import PromptTriggerRule from './PromptTriggerRule';
import styles from './PromptTriggerEditView.scss';
import PromptRuleTypes from './PromptRuleTypes';
import AddPromptRule from './AddPromptRule';

const PROMPT_VARIANT_COLUMNS = [
  {
    title: 'Title',
    width: '45%',
  },
  {
    title: 'User Message',
    width: '45%',
  },
  {
    title: '',
    width: '10%',
  },
];

const CLAUSE_OPTIONS = [
  {
    title: 'And',
    value: 'AND',
  },
  {
    title: 'Or',
    value: 'OR',
  },
];

export default class PromptTriggerEditView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      active: this.props.active,
      id: this.props.id,
      name: this.props.name,
      priority: this.props.priority,
      rules: this.props.rules,
      variants: this.props.variants,
      errors: {},
    };
  }

  render() {
    const clauseOption = CLAUSE_OPTIONS.filter(
      (opt) => opt.value === this.state.rules.clause
    )[0];
    return (
      <div className={styles.container}>
        <Text
          small
          label="Trigger Title (For Internal Use)"
          placeholder="Name this trigger"
          value={this.state.name}
          onChange={this._onChangeName}
          error={Boolean(this.state.errors.triggerName)}
          errorMessage={this.state.errors.triggerName}
        />
        <div className={`${styles.row} ${styles.spaceBetween}`}>
          <Checkbox.Item
            label="Active"
            value={this.state.active}
            onChange={this._onChangeActive}
          />
        </div>
        {this._renderPromptVariants()}
        <PromptRuleTypes
          options={CLAUSE_OPTIONS}
          value={clauseOption}
          onChange={(option) => this._onChangeRuleClause(option.value)}
          text="Prompt Rules Type"
        />

        {this._renderPromptRules()}
      </div>
    );
  }

  _renderPromptVariants = () => {
    const data = this.state.variants.map((variant, index) => {
      const row = {
        id: index,
        columns: [],
      };
      const error = (this.state.errors.variantItems || {})[index] || {};

      row.columns.push(
        this._renderVariantAndQuickReplyTitle(variant, index, error)
      );
      row.columns.push(
        this._renderVariantAndQuickReplyMessage(variant, index, error)
      );
      row.columns.push(this._renderVariantAndQuickReplyButtons(variant, index));
      return row;
    });

    return (
      <div className={styles.promptVariantsWrapper}>
        <div className={styles.promptVariantTitle}>Prompt Variations</div>
        <div className={styles.promptVariantAdd}>
          <Button size={Button.SIZES.SMALL} onClick={this._onAddVariant}>
            <Icon iconType="plus" /> Add New Variant <div />
          </Button>
        </div>
        {data.length > 0 && (
          <Table
            itemClassName={styles.variantTableItem}
            simple
            rows={data}
            columns={PROMPT_VARIANT_COLUMNS}
          />
        )}
      </div>
    );
  };

  /**
   * This method will render variant title and its QR title.
   * @param {Object} variant variant to render
   * @param {number} variantIndex index of variant
   * @param {string[]} error array of errors
   */
  _renderVariantAndQuickReplyTitle = (variant, variantIndex, error) => {
    return this._generateColumn(
      <div className={styles.variantContainer}>
        <Text
          className={styles.promptVariantInput}
          placeholder={`Add your prompt text here (max ${PROMPT_MAX_TITLE_LENGTH} characters)`}
          value={variant.message}
          onChange={(value) =>
            this._onChangeVariant(variantIndex, 'message', value)
          }
          error={Boolean(error.message)}
          errorMessage={error.message}
          errorMsgClassName={styles.inputErrorClass}
        />
        {variant.quick_replies &&
          variant.quick_replies.map((qr, qrIndex) => {
            const qrErrors = (error.quick_replies || {})[qrIndex] || {};
            return (
              <Text
                className={styles.quickReplyInput}
                placeholder={`Add a quick reply (max ${PROMPT_MAX_QUICK_REPLY_TITLE_LENGTH} characters)`}
                value={qr.message}
                onChange={(value) =>
                  this._onChangeVariantQuickReply(
                    variantIndex,
                    qrIndex,
                    'message',
                    value
                  )
                }
                error={Boolean(qrErrors.message)}
                errorMessage={qrErrors.message}
                errorMsgClassName={styles.inputErrorClass}
              />
            );
          })}
      </div>
    );
  };

  /**
   * This method will render variant message and its QR message.
   * @param {Object} variant variant to render
   * @param {number} variantIndex index of variant
   * @param {string[]} error array of errors
   */
  _renderVariantAndQuickReplyMessage = (variant, variantIndex, error) => {
    return this._generateColumn(
      <div className={styles.variantContainer}>
        <Text
          className={styles.promptVariantInput}
          placeholder="Message to be sent on behalf of the User"
          value={variant.gogo_message}
          onChange={(value) =>
            this._onChangeVariant(variantIndex, 'gogo_message', value)
          }
          error={Boolean(error.gogo_message)}
          errorMessage={error.gogo_message}
          errorMsgClassName={styles.inputErrorClass}
        />
        {variant.quick_replies &&
          variant.quick_replies.map((qr, qrIndex) => {
            const qrErrors = (error.quick_replies || {})[qrIndex] || {};
            return (
              <Text
                className={styles.quickReplyInput}
                placeholder="Quick reply message to be sent on behalf of the User"
                value={qr.gogo_message}
                onChange={(value) =>
                  this._onChangeVariantQuickReply(
                    variantIndex,
                    qrIndex,
                    'gogo_message',
                    value
                  )
                }
                error={Boolean(qrErrors.gogo_message)}
                errorMessage={qrErrors.gogo_message}
                errorMsgClassName={styles.inputErrorClass}
              />
            );
          })}
      </div>
    );
  };

  /**
   * This method will render variant and QR add and delete buttons.
   * @param {Object} variant variant to render
   * @param {number} variantIndex index of variant
   */
  _renderVariantAndQuickReplyButtons = (variant, variantIndex) => {
    return this._generateColumn(
      <div>
        <div className={styles.variantButtonContainer}>
          <div>
            <Icon
              iconType="trash"
              onClick={() => this._onRemoveVariant(variantIndex)}
            />
          </div>
          <div className={styles.addQuickReplyButton}>
            <Icon
              iconType="plus"
              onClick={() => this._onAddVariantQuickReply(variantIndex)}
            />
          </div>
        </div>
        {variant.quick_replies &&
          variant.quick_replies.map((_, qrIndex) => {
            return (
              <div className={styles.deleteVariantQuickReply}>
                <Icon
                  iconType="close"
                  onClick={() =>
                    this._onRemoveVariantQuickReply(variantIndex, qrIndex)
                  }
                />
              </div>
            );
          })}
      </div>
    );
  };

  /**
   * This will add a new QR in a specific variant.
   * @param {number} variantIndex index of variant in which new QR needs to be added
   */
  _onAddVariantQuickReply = (variantIndex) => {
    this._updateModalOnChange();
    const quickReplyArray =
      this.state.variants[variantIndex].quick_replies || [];
    if (quickReplyArray.length === PROMPT_MAX_QUICK_REPLY) {
      createAlert(
        `There should be at most ${PROMPT_MAX_QUICK_REPLY} Quick Replies.`,
        null,
        AlertTypes.NEGATIVE
      );
      return;
    }

    this.setState((prevState) => {
      const variants = prevState.variants.map((variant, index) => {
        if (index === variantIndex) {
          // For existing variant quick_replies array will be null.
          const quickReplies = variant.quick_replies || [];
          const newQuickReplies = quickReplies.concat({
            message: '',
            gogo_message: '',
          });
          return { ...variant, quick_replies: newQuickReplies };
        }
        return { ...variant };
      });

      return {
        variants,
      };
    });
  };

  /**
   * This method will update QR property of a specific variant.
   * @param {number} variantIndexToUpdate index of variant whose QR needs to be updated
   * @param {number} qrIndexToUpdadte index of QR whose property needs to be updated
   * @param {string} key key to update
   * @param {string} value value to update
   */
  _onChangeVariantQuickReply = (
    variantIndexToUpdate,
    qrIndexToUpdadte,
    key,
    value
  ) => {
    this._updateModalOnChange();

    this.setState((prevState) => {
      const variants = prevState.variants.map((variant, variantIndex) => {
        if (variantIndex === variantIndexToUpdate) {
          const updatedQRArray = variant.quick_replies.map((qr, qrIndex) => {
            if (qrIndex === qrIndexToUpdadte) {
              return { ...qr, [key]: value };
            }
            return qr;
          });
          return { ...variant, quick_replies: updatedQRArray };
        }
        return { ...variant };
      });
      return {
        variants,
      };
    });
  };

  /**
   * This method will remove QR from specific variant.
   * @param {number} variantIndexToUpdate index of variant whose QR needs to be removed
   * @param {number} qrIndexToRemove index of QR which needs to be removed
   */
  _onRemoveVariantQuickReply = (variantIndexToUpdate, qrIndexToRemove) => {
    this._updateModalOnChange();

    this.setState((prevState) => {
      const variants = prevState.variants.map((variant, variantIndex) => {
        if (variantIndex === variantIndexToUpdate) {
          const updatedQR = variant.quick_replies.filter(
            (_, qrIndex) => qrIndex !== qrIndexToRemove
          );
          return { ...variant, quick_replies: updatedQR };
        }
        return { ...variant };
      });

      return {
        variants,
      };
    });
  };

  _renderPromptRules = () => {
    const ruleList = this.state.rules.rule_list || [];
    return (
      <div className={styles.promptVariantsWrapper}>
        <AddPromptRule onClick={this._onAddRule} text="Prompt Rules" />
        {ruleList.map((rule, index) => {
          const error = (this.state.errors.ruleItems || {})[index] || {};
          return (
            <PromptTriggerRule
              supportedTypes={SUPPORTED_TYPES}
              supportedVariables={SUPPORTED_VARIABLES}
              supportedOperatorsByType={SUPPORTED_OPERATORS_BY_TYPE}
              supportedOperators={SUPPORTED_OPERATORS}
              fixedValueOperators={FIXED_VALUE_OPERATORS}
              error={error}
              key={index}
              {...rule}
              onChange={(key, value) => this._onChangeRule(index, key, value)}
              onRemove={() => this._onRemoveRule(index)}
            />
          );
        })}
      </div>
    );
  };

  _generateColumn = (content) => ({
    content,
  });

  _updateModalOnChange = () => {
    this.setState({ errors: {} });
    if (!this.state.edited) {
      this.setState({
        edited: true,
      });

      updateComponentModal({
        hasSaveButton: true,
        saveButtonText: 'Done',
        onSave: this._onSaveChanges,
        onIconClick: this._onClose,
      });
    }
  };

  _onChangeActive = () => {
    this._updateModalOnChange();
    this.setState((prevState) => ({
      active: !prevState.active,
    }));
  };

  _onChangeName = (value) => {
    this._updateModalOnChange();
    this.setState({
      name: value,
    });
  };

  _onChangeRule = (index, key, value) => {
    this._updateModalOnChange();
    this.setState((prevState) => {
      let rulesList = prevState.rules.rule_list;
      rulesList = rulesList.asMutable
        ? rulesList.asMutable({ deep: true })
        : rulesList;
      rulesList[index][key] = value;
      return {
        rules: {
          clause: prevState.rules.clause,
          rule_list: rulesList,
        },
      };
    });
  };

  _onAddRule = () => {
    this._updateModalOnChange();
    const defaultVariable = 'page_url';
    const defaultVariableType = SUPPORTED_VARIABLES[defaultVariable].type;
    const rulesList = this.state.rules.rule_list.concat({
      variable: defaultVariable,
      operator: SUPPORTED_OPERATORS_BY_TYPE[defaultVariableType][0],
      value: '',
      meta: {},
    });

    this.setState((prevState) => {
      const rules = {
        rule_list: rulesList,
        clause: prevState.rules.clause,
      };
      return {
        rules,
      };
    });
  };

  _onRemoveRule = (indexToRemove) => {
    this._updateModalOnChange();
    const rulesList = this.state.rules.rule_list.filter(
      (rule, index) => index !== indexToRemove
    );
    if (rulesList.length === 0) {
      createAlert('There should be atleast 1 rule', null, AlertTypes.NEGATIVE);
      return;
    }

    this.setState((prevState) => {
      const rules = {
        rule_list: rulesList,
        clause: prevState.rules.clause,
      };
      return {
        rules,
      };
    });
  };

  _onChangeRuleClause = (value) => {
    this._updateModalOnChange();
    this.setState((prevState) => ({
      rules: {
        clause: value,
        rule_list: prevState.rules.rule_list,
      },
    }));
  };

  _onChangeVariant = (indexToUpdate, key, value) => {
    this._updateModalOnChange();

    this.setState((prevState) => {
      const variants = prevState.variants.map((variant, index) => {
        if (index === indexToUpdate) return { ...variant, [key]: value };
        return { ...variant };
      });
      return {
        variants,
      };
    });
  };

  _onAddVariant = () => {
    this._updateModalOnChange();
    this.setState((prevState) => {
      const variants = prevState.variants.concat({
        message: '',
        actionable_text: '',
        gogo_message: '',
        quick_replies: [],
      });
      return {
        variants,
      };
    });
  };

  _onRemoveVariant = (indexToRemove) => {
    this._updateModalOnChange();
    const variants = this.state.variants.filter(
      (_, index) => index !== indexToRemove
    );
    if (variants.length === 0) {
      createAlert(
        'There should be atleast 1 Prompt Variant',
        null,
        AlertTypes.NEGATIVE
      );
      return;
    }

    this.setState((prevState) => {
      return {
        variants: prevState.variants.filter(
          (_, index) => index !== indexToRemove
        ),
      };
    });
  };

  _onSaveChanges = () => {
    if (this._validateData()) {
      this.props.onChange({
        active: this.state.active,
        id: this.state.id,
        name: this.state.name,
        priority: this.state.priority,
        rules: this.state.rules,
        variants: this.state.variants,
        trigger_type: this.props.trigger_type,
      });
      closeComponentModal();
    }
  };

  _onClose = () => {
    this.props.showConfirmationDialog({
      okTitle: 'Discard Changes',
      cancelTitle: 'Cancel',
      title: 'Do you want to discard the changes made in this Trigger?',
      message: 'Changes made for this trigger will be lost.',
      onOk: () => {
        closeComponentModal();
      },
      onCancel: () => {},
      okActionType: 'danger',
    });
  };

  /**
   * This will create a sub-array for variant errors with respect to the index of variant.
   * Every variant will have there own sub array for errors.
   * @param {string[]} errors array of errors
   * @param {number} variantIndex index of variant
   */
  _createVariantErrorArray = (errors, variantIndex) => {
    if (!errors.variantItems) {
      errors.variantItems = {};
    }
    if (!errors.variantItems[variantIndex]) {
      errors.variantItems[variantIndex] = {};
    }
  };

  /**
   * This will create a sub-array for quick reply errors with respect to the index of quick reply.
   * @param {string[]} errors array of errors
   * @param {number} variantIndex index of variant
   * @param {number} qrIndex index of quick reply
   */
  _createVariantQRErrorArray = (errors, variantIndex, qrIndex) => {
    this._createVariantErrorArray(errors, variantIndex);
    if (!errors.variantItems[variantIndex].quick_replies) {
      errors.variantItems[variantIndex].quick_replies = {};
    }
    if (!errors.variantItems[variantIndex].quick_replies[qrIndex]) {
      errors.variantItems[variantIndex].quick_replies[qrIndex] = {};
    }
  };

  _validateData = () => {
    const errors = {};
    const alerts = [];

    if (!this.state.name) {
      errors.triggerName = 'Please enter a valid Trigger Title';
    }

    if (!this.state.variants || this.state.variants.length === 0) {
      alerts.push('Please add atleast 1 Prompt Variant to this trigger');
    } else {
      this.state.variants.forEach((variant, index) => {
        if (!variant.message) {
          this._createVariantErrorArray(errors, index);
          errors.variantItems[index].message = 'Enter a valid message';
        }

        if (
          variant.message &&
          variant.message.length > PROMPT_MAX_TITLE_LENGTH
        ) {
          const message = `Character limit exceeded for title in variant ${
            index + 1
          }.`;
          alerts.push(message);
        }

        if (!variant.gogo_message) {
          this._createVariantErrorArray(errors, index);
          errors.variantItems[index].gogo_message =
            'Enter a valid User Message';
        }

        if (variant.quick_replies) {
          if (variant.quick_replies.length === 1) {
            const message = `There should be atleast 2 quick replies in variant ${
              index + 1
            }.`;
            alerts.push(message);
          }

          variant.quick_replies.forEach((quickReply, qrIndex) => {
            if (!quickReply.message) {
              this._createVariantQRErrorArray(errors, index, qrIndex);
              errors.variantItems[index].quick_replies[qrIndex].message =
                'Enter a valid Quick Reply message';
            }

            if (
              quickReply.message &&
              quickReply.message.length > PROMPT_MAX_QUICK_REPLY_TITLE_LENGTH
            ) {
              const message = `Character limit exceeded for quick reply title in variant ${
                index + 1
              }.`;
              alerts.push(message);
            }

            if (!quickReply.gogo_message) {
              this._createVariantQRErrorArray(errors, index, qrIndex);
              errors.variantItems[index].quick_replies[qrIndex].gogo_message =
                'Enter a valid Quick Reply User Message';
            }
          });
        }
      });
    }

    if (!this.state.rules) {
      alerts.push('Please configure the rules properly');
    } else if (
      !this.state.rules.clause ||
      ['AND', 'OR'].indexOf(this.state.rules.clause) === -1
    ) {
      alerts.push(
        'Please select an appropriate condtion type for the Prompt Rules'
      );
    } else if (
      !this.state.rules.rule_list ||
      this.state.rules.rule_list.length === 0
    ) {
      alerts.push('Please add atleast 1 rule for this trigger');
    } else {
      this.state.rules.rule_list.forEach((rule, index) => {
        if (!rule.variable) {
          if (!errors.ruleItems) {
            errors.ruleItems = {};
          }
          if (!errors.ruleItems[index]) {
            errors.ruleItems[index] = {};
          }
          errors.ruleItems[index].variable =
            'Please select an appropriate variable';
        }

        if (!rule.operator) {
          if (!errors.ruleItems) {
            errors.ruleItems = {};
          }
          if (!errors.ruleItems[index]) {
            errors.ruleItems[index] = {};
          }
          errors.ruleItems[index].operator =
            'Please select an appropriate Operator type';
        }

        if (!rule.value && !FIXED_VALUE_OPERATORS[rule.operator]) {
          if (!errors.ruleItems) {
            errors.ruleItems = {};
          }
          if (!errors.ruleItems[index]) {
            errors.ruleItems[index] = {};
          }
          errors.ruleItems[index].value = 'Please select an appropriate Value';
        }

        if (rule.variable) {
          const { metaKeys } = SUPPORTED_VARIABLES[rule.variable];
          metaKeys.forEach((metaKey) => {
            if (
              rule.meta[metaKey.key] === undefined ||
              rule.meta[metaKey.key] === ''
            ) {
              if (!errors.ruleItems) {
                errors.ruleItems = {};
              }
              if (!errors.ruleItems[index]) {
                errors.ruleItems[index] = {};
              }
              if (!errors.ruleItems[index].meta) {
                errors.ruleItems[index].meta = {};
              }
              errors.ruleItems[index].meta[metaKey.key] =
                'Please fill appropriate value for the meta field';
            }
          });
        }
      });
    }

    if (alerts.length > 0) {
      createAlert(alerts[0], null, AlertTypes.NEGATIVE);
    }
    this.setState({ errors });

    return Object.keys(errors).length === 0 && alerts.length === 0;
  };
}

PromptTriggerEditView.propTypes = {
  name: PropTypes.string.isRequired,
  rules: PropTypes.shape({
    clause: PropTypes.string.isRequired,
    rule_list: PropTypes.arrayOf(
      PropTypes.shape({
        variable: PropTypes.string.isRequired,
        operator: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
      })
    ),
  }).isRequired,
  priority: PropTypes.number.isRequired,
  active: PropTypes.bool.isRequired,
  variants: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string,
      actionable_text: PropTypes.string,
      gogo_message: PropTypes.string,
      quick_replies: PropTypes.arrayOf(
        PropTypes.shape({
          message: PropTypes.string,
          gogo_message: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  id: PropTypes.number,
  onChange: PropTypes.func,
  showConfirmationDialog: PropTypes.func,
};

PromptTriggerEditView.defaultProps = {
  id: -1,
  onChange: () => {},
  showConfirmationDialog: () => {},
};
